<template>
    <div class="rf-view">
        <NavigationBar :title="$t('settings.profiles.title')">
            <BackButton v-if="canPop" slot="left" @click="pop" />
        </NavigationBar>
        <main>
            <h1>
                {{ $t('settings.profileInformation.title') }}
            </h1>

            <GeneralErrorsView :error-box="errorBox" />

            <SplitForm>
                <div slot="left" class="container">
                    <InputBox :title="$t('fields.businessName')" :error-box="errorBox" error-fields="businessName">
                        <input v-model="businessName" class="input" :placeholder="$t('fields.placeholders.businessName').toString()" autocomplete="organization">
                    </InputBox>

                    <InputBox :title="$t('fields.VATNumber')" :error-box="errorBox" error-fields="VATNumber">
                        <input v-model="VATNumber" class="input" :placeholder="$t('fields.placeholders.VATNumber').toString()">
                    </InputBox>
                </div>
                <template slot="right">
                    <InputBox :title="$t('fields.contactName')" :error-box="errorBox" error-fields="contactName">
                        <input v-model="contactName" class="input" :placeholder="$t('fields.placeholders.contactName').toString()">
                    </InputBox>

                    <InputBox :title="$t('fields.contactPhone')" :error-box="errorBox" error-fields="contactPhone">
                        <input v-model="contactPhone" class="input" :placeholder="$t('fields.placeholders.contactPhone').toString()">
                    </InputBox>

                    <InputBox :title="$t('fields.city')" :error-box="errorBox" error-fields="city">
                        <input v-model="city" class="input" :placeholder="$t('fields.placeholders.city').toString()">
                    </InputBox>

                    <InputBox :title="$t('fields.postalCode')" :error-box="errorBox" error-fields="postalCode">
                        <input v-model="postalCode" class="input" :placeholder="$t('fields.placeholders.postalCode').toString()">
                    </InputBox>

                    <InputBox :title="$t('fields.address')" :error-box="errorBox" error-fields="address">
                        <input v-model="address" class="input" :placeholder="$t('fields.placeholders.address').toString()">
                    </InputBox>
                </template>
            </SplitForm>

            <hr>
            <InputBox :title="$t('onboarding.categories.title')" :error-box="errorBox" error-fields="emptyCategories">
                <List>
                    <ListItem v-for="category in categories" :key="category" element-name="label" :selectable="true">
                        {{ getCategoryName(category) }}

                        <Checkbox slot="right" v-model="selectedCategories" :value="category" name="category" />
                    </ListItem>
                </List>
            </InputBox>

            <template>
                <hr>
                <InputBox :title="$t('fields.wholesaler')" :error-box="errorBox">
                    <select v-model="wholesalerId" class="input">
                        <option v-for="wholesaler in wholesalers" :key="wholesaler.id" :value="wholesaler.id">
                            {{ wholesaler.settings.name }}
                        </option>
                    </select>
                </InputBox>
            </template>

            <Toolbar class="toolbar-wrapper">
                <LoadingButton :loading="saving">
                    <button class="primary button" @click="save">
                        {{ $t('buttons.save') }}
                    </button>
                </LoadingButton>
            </Toolbar>
        </main>
    </div>
</template>


<script lang="ts">
import { BackButton, Checkbox, InputBox, List, ListItem, LoadingButton, NavigationBar, Radio, SplitForm, Toolbar } from "@restofrit/components";
import { Session } from "@restofrit/networking";
import { Customer, CustomerCategory, CustomerCategoryHelper, Wholesaler } from "@restofrit/structures";
import { ArrayDecoder, Decoder } from "@simonbackx/simple-encoding";
import { ErrorBox, GeneralErrorsView, Validator } from "@simonbackx/simple-error-forms";
import { SimpleError, SimpleErrors } from "@simonbackx/simple-errors";
import { NavigationMixin } from "@simonbackx/vue-app-navigation";
import { Component, Mixins, Prop } from "vue-property-decorator";

@Component({
    components: {
        NavigationBar,
        InputBox,
        List, 
        ListItem,
        Radio,
        BackButton,
        GeneralErrorsView,
        Checkbox,
        SplitForm,
        Toolbar,
        LoadingButton
    },
})
export default class ProfileInformationEditView extends Mixins(NavigationMixin) {
    @Prop({ required: true })
    customer: Customer;

    saving = false;
    isChanged = false;
    errorBox: ErrorBox | null = null;
    validator = new Validator();

    wholesalers: Wholesaler[] = [];
    loadingWholesalers = false
    editedCustomer: Customer;

    mounted() {
        this.editedCustomer = this.customer.clone();
        this.loadWholesalers().catch(console.error)
    }

    async loadWholesalers() {
        this.loadingWholesalers = true
        try {
            const response = await Session.shared.authenticatedServer.request({
                method: "GET",
                path: "/customers/wholesalers",
                decoder: new ArrayDecoder(Wholesaler as Decoder<Wholesaler>)
            })
            this.wholesalers = response.data;
        } catch (e) {
            this.errorBox = new ErrorBox(e);
        }
        this.loadingWholesalers = false;
    }

    get wholesalerId() {
        return this.customer.wholesaler.id
    }

    set wholesalerId(wholesalerId: string) {
        this.isChanged = true;
        this.editedCustomer.wholesaler = this.wholesalers.find(w => w.id === wholesalerId)!;
    }

    shouldNavigateAway() {
        if (!this.isChanged) {
            return true
        }
        return confirm(this.$t('confirmation.noSave').toString())
    }

    get categories() {
        return Object.values(CustomerCategory);
    }

    getCategoryName(category: CustomerCategory) {
        return CustomerCategoryHelper.getName(category, this.$i18n as any);
    }

    get selectedCategories() {
        return this.customer.settings.categories;
    }

    set selectedCategories(categories: CustomerCategory[]) {
        this.isChanged = true;
        this.editedCustomer.settings.categories = categories;
    }

    // Getters and setters for customer properties
    get businessName() {
        return this.customer.settings.businessName
    }

    set businessName(businessName: string) {
        this.isChanged = true;
        this.editedCustomer.settings.businessName = businessName;
    }

    get VATNumber() {
        return this.customer.settings.VATNumber
    }

    set VATNumber(VATNumber: string) {
        this.isChanged = true;
        this.editedCustomer.settings.VATNumber = VATNumber;
    }

    get contactName() {
        return this.customer.settings.contactName
    }

    set contactName(contactName: string) {
        this.isChanged = true;
        this.editedCustomer.settings.contactName = contactName;
    }

    get contactPhone() {
        return this.customer.settings.contactPhone
    }

    set contactPhone(contactPhone: string) {
        this.isChanged = true;
        this.editedCustomer.settings.contactPhone = contactPhone;
    }

    get city() {
        return this.customer.settings.city
    }

    set city(city: string) {
        this.isChanged = true;
        this.editedCustomer.settings.city = city;
    }

    get postalCode() {
        return this.customer.settings.postalCode
    }

    set postalCode(postalCode: string) {
        this.isChanged = true;
        this.editedCustomer.settings.postalCode = postalCode;
    }

    get address() {
        return this.customer.settings.address
    }

    set address(address: string) {
        this.isChanged = true;
        this.editedCustomer.settings.address = address;
    }
    
    async save() {
        console.log("Save...");
        
        if (this.saving) {
            return
        }
        this.saving = true
        this.errorBox = null
        this.customer = this.editedCustomer.clone();

        try {
            const errors = new SimpleErrors();

            // Validate that the categories list is not empty
            if (!this.customer.settings.categories || this.customer.settings.categories.length === 0) {
                errors.addError(new SimpleError({
                    code: "emptyCategory",
                    message: "Choose at least one category",
                    human: this.$t('errors.emptyCategories').toString(),
                    field: "emptyCategories"
                }))
                this.saving = false;
            }

            errors.throwIfNotEmpty();

            const response = await Session.shared.authenticatedServer.request({
                method: "PATCH",
                path: "/customers/" + this.customer.id,
                body: {
                    businessName: this.businessName,
                    VATNumber: this.VATNumber,
                    contactName: this.contactName,
                    contactPhone: this.contactPhone,
                    categories: this.selectedCategories,
                    address: this.address,
                    postalCode: this.postalCode,
                    city: this.city,
                    wholesalerId: this.wholesalerId
                },
                decoder: Customer as Decoder<Customer>
            })
            window.location.reload();
        } catch (e) {
            this.errorBox = new ErrorBox(e)
        }
        this.saving = false
    }
}
</script>

<style scoped>
    .toolbar-wrapper {
        margin-bottom: 5px
    }
</style>